/**
 * Supabase Realtime Manager
 *
 * Replaces Socket.IO with Supabase Channels for:
 * - Presence: player join/leave/avatar state
 * - Broadcast: movement packets (~15Hz), world object CRUD, chat
 *
 * One channel per world. Singleton pattern like the old socketManager.
 */

import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';

let currentChannel: RealtimeChannel | null = null;
let currentWorldId: string | null = null;
let currentPresenceKey: string | null = null;

// A RealtimeChannel only honours its first subscribe() call: a second caller's
// status callback never fires, so its join announcement never goes out. All
// consumers go through onChannelSubscribed() instead, which subscribes once
// and fans the status out.
type StatusListener = (status: string, err?: Error) => void;
let statusListeners = new Set<StatusListener>();
let subscribed = false;
let subscribeRequested = false;

export interface PlayerPresence {
  id: string; // unique per tab (presence key)
  avatarUrl: string;
  userName: string;
  userId: string | null;
  joinedAt: number;
}

export function getChannel(worldId: string): RealtimeChannel {
  if (currentChannel && currentWorldId === worldId) {
    return currentChannel;
  }

  // Cleanup old channel
  if (currentChannel) {
    supabase.removeChannel(currentChannel);
    currentChannel = null;
    currentWorldId = null;
    currentPresenceKey = null;
    statusListeners = new Set();
    subscribed = false;
    subscribeRequested = false;
  }

  currentPresenceKey = crypto.randomUUID();
  currentChannel = supabase.channel(`world:${worldId}`, {
    config: {
      presence: { key: currentPresenceKey },
      broadcast: { self: false }, // don't receive own broadcasts
    },
  });

  currentWorldId = worldId;
  return currentChannel;
}

export function getPresenceKey(): string | null {
  return currentPresenceKey;
}

export function disconnectChannel() {
  if (currentChannel) {
    supabase.removeChannel(currentChannel);
    currentChannel = null;
    currentWorldId = null;
    currentPresenceKey = null;
    statusListeners = new Set();
    subscribed = false;
    subscribeRequested = false;
  }
}

export function getCurrentChannel(): RealtimeChannel | null {
  return currentChannel;
}

export function getCurrentWorldId(): string | null {
  return currentWorldId;
}

/**
 * Run `listener` with each status of the world channel, subscribing it if no
 * one has yet. If the channel is already joined, the listener is called with
 * 'SUBSCRIBED' straight away. Register `channel.on(...)` handlers first.
 * Returns a function that removes the listener.
 */
export function onChannelSubscribed(worldId: string, listener: StatusListener): () => void {
  const channel = getChannel(worldId);
  statusListeners.add(listener);

  if (subscribed) {
    listener('SUBSCRIBED');
  } else if (!subscribeRequested) {
    subscribeRequested = true;
    const listeners = statusListeners;
    channel.subscribe((status, err) => {
      if (channel !== currentChannel) return; // replaced or disconnected meanwhile
      subscribed = status === 'SUBSCRIBED';
      listeners.forEach((l) => l(status, err));
    });
  }

  return () => { statusListeners.delete(listener); };
}
